import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, AreaF, ImageF } from "./AFields";
import { Modal } from "./Participants";

export default function SeoManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/seo").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/admin/seo/save", editing);
      toast.success("SEO saved — live immediately");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-seo">
      <SEO title="SEO Manager" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">SEO Management</h1>
          <p className="mt-1 text-sm text-slate-500">Per-page SEO title, meta description, keywords and social sharing image. Sitemap is auto-generated at /api/public/sitemap.xml.</p>
        </div>
        <button onClick={() => setEditing({ path: "/", title: "", description: "", keywords: "", image: "" })} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="seo-new-btn"><Plus size={16} /> Add Page</button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-gray-200 bg-navy-50/50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Page Path</th><th className="px-4 py-3">SEO Title</th><th className="px-4 py-3">Meta Description</th><th className="px-4 py-3 text-right">Edit</th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-gray-100 last:border-0" data-testid={`seo-row-${p.id}`}>
                <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{p.path}</td>
                <td className="max-w-[220px] truncate px-4 py-3">{p.title || <span className="text-slate-400">default</span>}</td>
                <td className="max-w-[280px] truncate px-4 py-3 text-slate-500">{p.description || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(p)} className="rounded-md border border-gray-200 p-2 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`seo-edit-${p.id}`}><Pencil size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={`SEO — ${editing.path}`} testid="seo-editor">
          <div className="space-y-5">
            <TextF label="Page Path" value={editing.path} onChange={(v) => setEditing({ ...editing, path: v })} required />
            <TextF label="SEO Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
            <AreaF label="Meta Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} rows={3} />
            <TextF label="Keywords (comma separated)" value={editing.keywords} onChange={(v) => setEditing({ ...editing, keywords: v })} />
            <ImageF label="Social Sharing Image" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={save} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="seo-save">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
